import React, { useEffect, useState } from "react";
import { PageHeader } from "@/components/PageHeader";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, Mail, Phone, ShieldCheck, MapPin, Link as LinkIcon, Pencil, Archive, ArchiveRestore } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { useSelection, RowBox, SelectAllBox, BulkBar } from "@/components/BulkSelect";

export default function Servers() {
  const [list, setList] = useState([]);
  const [users, setUsers] = useState([]);
  const [editing, setEditing] = useState(null); // null | {} (new) | server
  const [showInactive, setShowInactive] = useState(false);
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const load = () => {
    api.get("/servers").then((r) => setList(r.data));
    if (isAdmin) api.get("/users").then((r) => setUsers(r.data.filter((u) => u.approved)));
  };
  useEffect(() => { load(); }, []);
  const link = async (serverId, userId) => {
    await api.patch(`/servers/${serverId}`, { linked_user_id: userId === "none" ? null : userId });
    toast.success(userId === "none" ? "Login unlinked" : "Login linked"); load();
  };
  const visible = list.filter((s) => showInactive ? s.active === false : s.active !== false);
  const selection = useSelection(visible.map((s) => s.id));
  const bulk = async (ids, action, msg) => {
    try { await api.post("/servers/bulk", { ids, action }); toast.success(`${ids.length} server(s) ${msg}`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Bulk action failed"); }
  };
  const bulkActions = [
    showInactive ? { label: "Restore", icon: ArchiveRestore, onClick: (ids) => bulk(ids, "restore", "restored") }
      : { label: "Archive", icon: Archive, confirm: "Archive {n} server(s)? They will be hidden and marked inactive.", onClick: (ids) => bulk(ids, "archive", "archived") },
    { label: "Delete", icon: Trash2, className: "text-rose-600 border-rose-200 hover:bg-rose-50", confirm: "Permanently delete {n} server(s)?", onClick: (ids) => bulk(ids, "delete", "deleted") },
  ];

  return (
    <>
      <PageHeader
        title="Servers"
        description="Your field servers with current workload. Link a Google login so a server sees only their own jobs."
        actions={isAdmin ? (
          <Button data-testid="new-server-button" onClick={() => setEditing({})} className="sm-green">
            <Plus className="h-4 w-4 mr-2" /> Add server
          </Button>
        ) : null}
      />
      <div className="p-4 sm:p-7 space-y-4">
        <div className="flex items-center gap-4 text-sm">
          {isAdmin && <label className="flex items-center gap-2 cursor-pointer"><SelectAllBox selection={selection} /> Select all</label>}
          <button type="button" className="sm-link text-sm ml-auto" data-testid="toggle-inactive-servers" onClick={() => setShowInactive((v) => !v)}>{showInactive ? "Show active servers" : `Show archived (${list.filter((s) => s.active === false).length})`}</button>
        </div>
        {isAdmin && <BulkBar selection={selection} noun="server" actions={bulkActions} />}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="servers-grid">
          {visible.length === 0 ? (
            <div className="col-span-full text-center py-14 text-slate-400">{showInactive ? "No archived servers." : "No servers on the roster."}</div>
          ) : visible.map((s) => (
            <Card key={s.id} className={`data-card border-slate-200 p-5 ${selection.selected.has(s.id) ? "ring-2 ring-[#2d7bd0]/40" : ""}`} data-testid={`server-card-${s.id}`}>
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-3">
                  {isAdmin && <div className="pt-1"><RowBox selection={selection} id={s.id} /></div>}
                  <div>
                    <div className="sm-label text-emerald-700">Field agent{s.active === false ? " · archived" : ""}</div>
                    <h3 className="mt-0.5 text-base font-semibold text-slate-900">{s.name}</h3>
                    <div className="mt-1 flex items-center gap-1 text-xs text-slate-500"><ShieldCheck className="h-3.5 w-3.5 text-emerald-600" />License {s.license_number || "—"}</div>
                  </div>
                </div>
                {isAdmin && (
                  <div className="flex gap-1">
                    <Button variant="ghost" size="sm" data-testid={`edit-server-${s.id}`} className="text-slate-400 hover:text-slate-900 h-8 w-8 p-0" onClick={() => setEditing(s)}><Pencil className="h-4 w-4" /></Button>
                    <Button variant="ghost" size="sm" data-testid={`delete-server-${s.id}`} className="text-slate-400 hover:text-rose-600 h-8 w-8 p-0" onClick={async () => {
                      if (!window.confirm("Delete this server?")) return;
                      await api.delete(`/servers/${s.id}`); toast.success("Deleted"); load();
                    }}><Trash2 className="h-4 w-4" /></Button>
                  </div>
                )}
              </div>
              <div className="mt-4 space-y-1.5 text-xs text-slate-600">
                <div className="flex items-center gap-2"><Mail className="h-3.5 w-3.5 text-slate-400" />{s.email}</div>
                {s.phone ? <div className="flex items-center gap-2"><Phone className="h-3.5 w-3.5 text-slate-400" />{s.phone}</div> : null}
                {s.counties?.length ? <div className="flex items-center gap-2"><MapPin className="h-3.5 w-3.5 text-slate-400" />{s.counties.join(", ")}</div> : null}
              </div>
              <div className="mt-4 pt-4 border-t border-slate-100 flex items-center justify-between">
                <div className="sm-label">Active jobs</div>
                <div className="text-2xl font-semibold text-slate-900">{s.active_jobs}</div>
              </div>
              <div className="mt-3 pt-3 border-t border-slate-100">
                <div className="sm-label mb-1 flex items-center gap-1"><LinkIcon className="h-3 w-3" /> Login account</div>
                {isAdmin ? (
                  <Select value={s.linked_user_id || "none"} onValueChange={(v) => link(s.id, v)}>
                    <SelectTrigger className="h-8 text-xs" data-testid={`link-user-select-${s.id}`}><SelectValue placeholder="Not linked" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Not linked</SelectItem>
                      {users.map((u) => <SelectItem key={u.user_id} value={u.user_id}>{u.name}<span className="text-slate-400"> · {u.email}</span></SelectItem>)}
                    </SelectContent>
                  </Select>
                ) : <div className="text-xs text-slate-600" data-testid={`linked-user-${s.id}`}>{s.linked_user ? s.linked_user.name || s.linked_user.email : "Not linked"}</div>}
                {s.linked_user && <div className="mt-1 text-[11px] text-slate-400">Shown as “{s.name}” across the app · they can change it under Settings → Profile</div>}
                {!s.linked_user_id && (() => { const m = users.find((u) => s.email && u.email?.toLowerCase() === s.email.toLowerCase()); return (
                  <div className="mt-1.5 rounded border border-amber-300 bg-amber-50 px-2 py-1.5 text-[11px] text-amber-900" data-testid={`unlinked-warning-${s.id}`}>
                    <b>Not linked to a login</b> — {s.name} won't see assigned jobs or documents when they sign in.
                    {m ? <> {isAdmin ? <button type="button" className="underline font-semibold" onClick={() => link(s.id, m.user_id)} data-testid={`link-match-${s.id}`}>Link to {m.email}</button> : <>A matching account exists ({m.email}); ask an admin to link it.</>}</> : s.email ? <> No account with {s.email} yet — it links automatically once they sign in with that email.</> : <> Add their email above so the link happens automatically.</>}
                  </div>); })()}
              </div>
            </Card>
          ))}
        </div>
      </div>
      {editing !== null && <ServerDialog server={editing.id ? editing : null} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </>
  );
}

function ServerDialog({ server, onClose, onSaved }) {
  const [f, setF] = useState({ name: server?.name || "", email: server?.email || "", phone: server?.phone || "", license_number: server?.license_number || "", counties: (server?.counties || []).join(", ") });
  const submit = async (e) => {
    e.preventDefault();
    if (!f.name || !f.email) return toast.error("Name and email required");
    const body = { ...f, counties: f.counties.split(",").map((s) => s.trim()).filter(Boolean) };
    try {
      if (server) await api.patch(`/servers/${server.id}`, body); else await api.post("/servers", body);
      toast.success(server ? "Server updated" : "Server added"); onSaved();
    } catch { toast.error("Failed"); }
  };
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="server-dialog">
        <DialogHeader><DialogTitle>{server ? `Edit ${server.name}` : "Add process server"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <Row label="Full name *"><Input data-testid="server-name" value={f.name} onChange={set("name")} /></Row>
          <Row label="Email *"><Input data-testid="server-email" type="email" value={f.email} onChange={set("email")} /></Row>
          <div className="grid grid-cols-2 gap-3">
            <Row label="Phone"><Input value={f.phone} onChange={set("phone")} /></Row>
            <Row label="License number"><Input value={f.license_number} onChange={set("license_number")} /></Row>
          </div>
          <Row label="Counties (comma-separated)"><Input placeholder="New Castle, Kent" value={f.counties} onChange={set("counties")} /></Row>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button data-testid="create-server-submit" type="submit" className="sm-green">{server ? "Save" : "Add"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
function Row({ label, children }) {
  return <div className="space-y-1"><Label className="sm-label">{label}</Label>{children}</div>;
}
